import { AlertTriangle, Check, CloudOff, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { usePersistenceStatus, type PersistenceStatus } from '@/state/persistenceStatus';

/**
 * Always-on top-bar chip reflecting the write-through persistence layer
 * (`api/persist.ts` → `usePersistenceStatus`). Persistence is optimistic, so
 * this is the *only* signal that a write failed — it replaces the old silent
 * `.catch(() => {})`. Amber/red states are the load-bearing ones: they tell the
 * operator their edits are not reaching the server.
 */

interface ChipStyle {
  icon: typeof Check;
  label: string;
  className: string;
  spin?: boolean;
  testStatus: string;
}

const STYLES: Record<PersistenceStatus, ChipStyle> = {
  idle: {
    icon: Check,
    label: 'Ready',
    className: 'border-gold-dim/30 text-cream-faint',
    testStatus: 'idle',
  },
  saving: {
    icon: Loader2,
    label: 'Saving…',
    className: 'border-gold-dim/30 text-cream-soft',
    spin: true,
    testStatus: 'saving',
  },
  saved: {
    icon: Check,
    label: 'Saved',
    className: 'border-mint/40 text-mint',
    testStatus: 'saved',
  },
  offline: {
    icon: CloudOff,
    label: 'Not saving — API offline',
    className: 'border-orange/50 bg-orange/10 text-orange',
    testStatus: 'offline',
  },
  // The server answered and REFUSED a write (409 / 422 / any 4xx) — the API is
  // up, so never "offline" (tv-2na.8.5). The label carries the server's reason.
  rejected: {
    icon: AlertTriangle,
    label: 'Not saved — change refused',
    className: 'border-blood/50 bg-blood/10 text-blood',
    testStatus: 'rejected',
  },
  error: {
    icon: CloudOff,
    label: 'Not saving — write failed',
    className: 'border-blood/50 bg-blood/10 text-blood',
    testStatus: 'error',
  },
};

export default function PersistenceIndicator() {
  const status = usePersistenceStatus((s) => s.status);
  const reason = usePersistenceStatus((s) => s.reason);
  const style = STYLES[status];
  const Icon = style.icon;
  const label = status === 'rejected' && reason ? `Not saved — ${reason}` : style.label;

  return (
    <div
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs whitespace-nowrap',
        style.className,
      )}
      data-testid="persistence-indicator"
      data-status={style.testStatus}
      role="status"
      aria-live="polite"
      title={label}
    >
      <Icon size={13} className={clsx('shrink-0', style.spin && 'animate-spin')} />
      <span className="truncate max-w-[22rem]">{label}</span>
    </div>
  );
}
